import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import { findService, SERVICES } from "../data/services";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";
import MagneticButton from "../components/MagneticButton";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const service = findService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  const SITE = getSiteOrigin();
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
      { "@type": "ListItem", position: 3, name: service.title[lang], item: `${SITE}/services/${slug}` },
    ],
  };

  // FAQ schema per service for GEO / AI citation readiness
  const faqData = {
    "video-production": {
      fr: [
        { q: "Combien coûte une vidéo de marque à Moncton ?", a: "Un film de marque à Moncton coûte entre 1 800 $ CAD (forfait Essentiel, demi-journée) et 4 500 $ CAD (forfait Signature, journée complète + direction artistique). Les productions Cinema sur mesure sont disponibles sur devis. HST 15 % en sus au N.-B." },
        { q: "Quel est le délai de livraison pour une production vidéo ?", a: "Le délai standard est de 10 jours ouvrables pour le forfait Essentiel et 15–20 jours pour le forfait Signature. Des délais accélérés sont possibles sur demande." },
        { q: "Travaillez-vous dans toute la province du Nouveau-Brunswick ?", a: "Oui. Phaneroo Prod est basé à Moncton et intervient dans tout le Nouveau-Brunswick — Fredericton, Saint John, Shediac, Bathurst — ainsi qu'en Nouvelle-Écosse, à l'Î.-P.-É. et au Québec." },
      ],
      en: [
        { q: "How much does a brand video cost in Moncton?", a: "A brand film in Moncton costs between CAD $1,800 (Essential package, half-day) and CAD $4,500 (Signature package, full day + art direction). Custom Cinema productions are available on quote. 15% HST extra in NB." },
        { q: "What is the turnaround time for video production?", a: "Standard turnaround is 10 business days for the Essential package and 15–20 days for the Signature package. Rush delivery is available on request." },
        { q: "Do you work across New Brunswick?", a: "Yes. Phaneroo Prod is based in Moncton and works across New Brunswick — Fredericton, Saint John, Shediac, Bathurst — as well as Nova Scotia, PEI and Quebec." },
      ],
    },
    "photography": {
      fr: [
        { q: "Combien coûte une séance photo éditoriale à Moncton ?", a: "Les séances photo de Phaneroo Prod commencent à 1 800 $ CAD pour un forfait Essentiel (20–40 images retouchées haute résolution avec licence commerciale illimitée)." },
        { q: "Faites-vous la photographie d'entreprise et de portrait à Moncton ?", a: "Oui. Nous photographions les équipes, les fondateurs, les produits, les espaces et les événements corporatifs à Moncton et dans tout le Canada Atlantique." },
      ],
      en: [
        { q: "How much does editorial photography cost in Moncton?", a: "Phaneroo Prod photography sessions start at CAD $1,800 for an Essential package (20–40 retouched high-res images with unlimited commercial license)." },
        { q: "Do you do corporate and portrait photography in Moncton?", a: "Yes. We photograph teams, founders, products, spaces and corporate events in Moncton and across Atlantic Canada." },
      ],
    },
    "drone-aerial": {
      fr: [
        { q: "Avez-vous le certificat RPAS de Transports Canada pour le drone à Moncton ?", a: "Oui. Phaneroo Prod est opéré par un pilote certifié niveau avancé RPAS de Transports Canada. Toutes les prises de vue incluent plan de vol, autorisations Nav Canada et assurance responsabilité commerciale de 5 M$." },
        { q: "Pouvez-vous filmer avec un drone dans l'espace aérien contrôlé près de l'aéroport de Moncton (YQM) ?", a: "Oui, avec les autorisations appropriées de Transports Canada et Nav Canada. Nous gérons tout le processus d'autorisation pour vous." },
      ],
      en: [
        { q: "Are you Transport Canada RPAS certified for drone work in Moncton?", a: "Yes. Phaneroo Prod is operated by a Transport Canada Advanced RPAS-certified pilot. All aerial work includes flight plan, Nav Canada authorizations and $5M commercial liability insurance." },
        { q: "Can you film drone footage in controlled airspace near Moncton Airport (YQM)?", a: "Yes, with the appropriate Transport Canada and Nav Canada authorizations. We handle the full permit process for you." },
      ],
    },
    "social-content": {
      fr: [
        { q: "Proposez-vous des forfaits mensuels de contenu vidéo pour les réseaux sociaux ?", a: "Oui. Nos forfaits de contenu social incluent 8 à 16 capsules verticales par mois, sous-titres bilingues FR/EN, calendrier éditorial et rapport de performance mensuel." },
      ],
      en: [
        { q: "Do you offer monthly social media video content packages?", a: "Yes. Our social content retainers include 8–16 vertical capsules per month, bilingual FR/EN captions, editorial calendar and monthly performance report." },
      ],
    },
    "art-direction": {
      fr: [
        { q: "Qu'est-ce que la direction artistique comprend chez Phaneroo Prod ?", a: "La direction artistique comprend un manuel de marque visuelle, des guides de tournage et de retouche, la direction photo et la scénarisation, ainsi que des templates motion et sociaux pour une cohérence multi-canal." },
      ],
      en: [
        { q: "What does art direction include at Phaneroo Prod?", a: "Art direction includes a visual brand book, shoot and retouch guidelines, photo direction and scripting, plus motion and social templates for cross-channel consistency." },
      ],
    },
  };

  const serviceFaqs = faqData[slug];
  const faqSchema = serviceFaqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (serviceFaqs[lang] || serviceFaqs.en).map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  } : null;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
    name: service.title[lang],
    serviceType: service.title[lang],
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE}/#studio`,
      name: "Phaneroo Prod",
      telephone: "+1-506-871-6122",
      email: "contact@phanerooventures.com",
      address: { "@type": "PostalAddress", addressLocality: "Moncton", addressRegion: "NB", postalCode: "E1C 1A1", addressCountry: "CA" },
    },
    description: service.seo_desc[lang],
    areaServed: ["Canada", "New Brunswick", "Nova Scotia", "Prince Edward Island", "Quebec"],
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    image: service.cover.startsWith("http") ? service.cover : `${SITE}${service.cover}`,
    offers: {
      "@type": "Offer",
      priceCurrency: "CAD",
      availability: "https://schema.org/InStock",
      url: `${SITE}/services/${slug}`,
    },
    },
    breadcrumb,
    ...(faqSchema ? [faqSchema] : []),
  ];

  return (
    <main className="bg-[#050505] min-h-screen" data-testid={`page-service-${slug}`}>
      <SEO
        title={service.title[lang]}
        description={service.seo_desc[lang]}
        path={`/services/${slug}`}
        image={service.cover}
        lang={lang}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative pt-40 lg:pt-48 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 z-0">
          <img src={service.cover} alt={service.title[lang]} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent lg:from-[#050505]/95 lg:via-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-100 text-[11px] tracking-[0.22em] uppercase mb-10" data-testid="back-to-services">
            <ArrowLeft size={14} /> {lang === "fr" ? "Tous les services" : "All services"}
          </Link>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
            <span className="block w-8 h-px bg-rouge" /><span>{service.tag} · {service.title[lang]}</span>
          </div>
          <h1 className="mt-6 font-serif-editorial text-4xl sm:text-5xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02] max-w-3xl">
            {service.hero[lang]}
          </h1>
          <p className="mt-8 max-w-xl text-ink-300 text-base lg:text-lg leading-relaxed">{service.intro[lang]}</p>
          {slug === "drone-aerial" && (
            <p className="mt-5 max-w-xl text-[11px] tracking-[0.18em] uppercase text-ink-400 leading-relaxed">
              {lang === "fr"
                ? "Transport Canada · Pilote RPAS certifié (niveau avancé) · Assurance responsabilité civile commerciale · Risques aviation couverts selon police du studio"
                : "Transport Canada · Advanced-certified RPAS pilot · Commercial liability coverage · Aviation risks covered per studio policy"}
            </p>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid={`service-cta-${slug}`}
              data-cursor={service.cta[lang]}
              className="group inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep text-ink-100 px-7 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors"
            >
              {service.cta[lang]} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </MagneticButton>
            <Link to="/portfolio" className="text-ink-300 hover:text-ink-100 text-[11px] tracking-[0.24em] uppercase border-b border-ink-500 pb-1" data-testid={`service-view-work-${slug}`}>
              {lang === "fr" ? "Voir le travail" : "View the work"} ↗
            </Link>
          </div>
        </div>
      </section>

      {/* Deliverables + Use cases */}
      <section className="py-20 lg:py-28 border-t border-hair">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 mb-6 flex items-center gap-3">
              <span className="block w-8 h-px bg-rouge" />{lang === "fr" ? "Livrables" : "Deliverables"}
            </div>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl text-ink-100 leading-tight mb-10">
              {lang === "fr" ? "Ce qui est inclus." : "What you get."}
            </h2>
            <ul className="space-y-4">
              {service.deliverables[lang].map((d) => (
                <li key={d} className="flex items-start gap-4 text-ink-100 border-b border-hair pb-4">
                  <Check size={16} className="text-rouge mt-1 shrink-0" />
                  <span className="text-base lg:text-lg">{d}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 mb-6 flex items-center gap-3">
              <span className="block w-8 h-px bg-rouge" />{lang === "fr" ? "Cas d'usage" : "Use cases"}
            </div>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl text-ink-100 leading-tight mb-10">
              {lang === "fr" ? "Pour qui ?" : "For whom?"}
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.use_cases[lang].map((u, i) => (
                <li key={u} className="border border-hair p-5 text-ink-100 text-sm lg:text-base">
                  <span className="text-rouge font-serif-editorial text-xl mr-3">0{i + 1}</span>{u}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ Section — GEO/AI citation readiness */}
      {serviceFaqs && (serviceFaqs[lang] || serviceFaqs.en).length > 0 && (
        <section className="py-20 lg:py-28 border-t border-hair" aria-label={lang === "fr" ? "Questions fréquentes" : "Frequently asked questions"}>
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <Reveal>
              <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 mb-6 flex items-center gap-3">
                <span className="block w-8 h-px bg-rouge" />
                {lang === "fr" ? "Questions fréquentes" : "Frequently asked questions"}
              </div>
              <h2 className="font-serif-editorial text-3xl sm:text-4xl text-ink-100 leading-tight mb-10">
                {lang === "fr" ? "Ce que les clients demandent." : "What clients ask."}
              </h2>
            </Reveal>
            <dl className="divide-y divide-hair max-w-3xl">
              {(serviceFaqs[lang] || serviceFaqs.en).map((item, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="py-6">
                    <dt className="font-serif-editorial text-xl text-ink-100 mb-3">{item.q}</dt>
                    <dd className="text-ink-300 text-base leading-relaxed">{item.a}</dd>
                  </div>
                </Reveal>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* CTA band */}
      <section className="py-24 lg:py-32 border-t border-hair">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h3 className="font-serif-editorial text-3xl sm:text-5xl lg:text-6xl text-ink-100 leading-[1.05]">
              {lang === "fr" ? "Une idée. Un brief. Un devis sous 24h." : "An idea. A brief. A quote within 24h."}
            </h3>
            <p className="mt-4 text-ink-300 max-w-xl">{lang === "fr" ? "Appel exploratoire gratuit de 20 minutes. Aucun engagement." : "Free 20-minute discovery call. No commitment."}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid={`service-cta-bottom-${slug}`}
              data-cursor={service.cta[lang]}
              className="group inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep text-ink-100 px-8 py-5 text-[11px] tracking-[0.24em] uppercase"
            >
              {service.cta[lang]} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-24 border-t border-hair">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-ink-300 mb-10 flex items-center gap-3">
            <span className="block w-8 h-px bg-rouge" />{lang === "fr" ? "Autres services" : "Other services"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                data-testid={`related-service-${r.slug}`}
                data-cursor={r.title[lang]}
                className="group relative border border-hair hover:border-ink-100 transition-colors overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.cover} alt={r.title[lang]} className="w-full h-full object-cover saturate-50 group-hover:saturate-100 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">{r.tag}</div>
                  <div className="mt-2 font-serif-editorial text-2xl text-ink-100">{r.title[lang]}</div>
                  <div className="mt-3 text-ink-300 text-sm line-clamp-2">{r.subtitle[lang]}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
